// src/componentsV2/Acciones/Finiquitar.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card, CardContent, Typography, Grid, Divider, Box, Checkbox, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, FormControl, InputLabel, MenuItem, Select, LinearProgress, Stack
} from '@mui/material';
import moment from 'moment';
import { FiniquitoService } from '../../services/finiquitoServiceV2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from 'sweetalert2';
import html2pdf from 'html2pdf.js';
import Cookies from 'universal-cookie';
import { setCargaDatos } from '../../Redux/V2/ContratoRedux';
import { setCargaGeneral } from '../../Redux/GeneralDuck';

const formatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' });

const normalizeTemplate = (tpl) => ({
  id: tpl?.id ?? tpl?.Id ?? tpl?.ID,
  code: String(tpl?.code ?? tpl?.Code ?? '').trim(),
  name: tpl?.name ?? tpl?.Name ?? '',
  html: tpl?.html ?? tpl?.Html ?? '',
});

const Finiquitar = ({ setHide }) => {
  const cookies = new Cookies();
  const dispatch = useDispatch();

  // ===== Redux State =====
  const entrevista = useSelector((store) => store.contratoV2.Entrevista);
  const serviciosRedux = useSelector((store) => store.contratoV2.contrato);
  const pagador = useSelector((store) => store.contratoV2.Pagador);
  const defendido = useSelector((store) => store.contratoV2.Defendido);
  const general = useSelector((store) => store.general);
  const contratoFiniquitado = useSelector((store) => store.contratoV2.ContratoFiniquitado);

  // ===== UI State =====
  const [atencion, setAtencion] = useState({
    Atendidopor: '',
    FechaAtencion: '',
    NombrePagador: '',
    RutPagador: '',
    TelefonoPagador: '',
    EmailPagador: '',
    RegionPagador: '',
    DireccionPagador: '',
  });
  const [formatos, setFormatos] = useState([]);
  const [causas, setCausas] = useState([]);
  const [finiquito, setFiniquito] = useState(null);

  // Solo fallback por servicio (NO store)
  const [cuotasMan, setCuotasMan] = useState([]);
  const [cuotasShow, setCuotasShow] = useState(false);
  const [searching, setSearching] = useState(true);

  const [finiquitado, setFiniquitado] = useState(false);
  const [formatoSeleccionado, setFormatoSeleccionado] = useState('');
  const [htmlSeleccionado, setHtmlSeleccionado] = useState('');

  // ===== Helpers =====
  const pickFechaEntrevista = (ent) => {
    const raw = ent?.fecha ?? ent?.createAt ?? ent?.date ?? ent?.dateContact ?? null;
    return raw ? moment(raw).format('DD-MM-YYYY HH:mm') : '';
  };

  useEffect(() => {
    const keys = ["negocios", "sucursales"];//,"generos", "estadoCivil", "paises", "regiones", "provincias", "comunas"];
    keys.forEach((key) => dispatch(setCargaGeneral(key)));
  }, [])

  // Cargas iniciales mínimas (sin cuotas via store)
  useEffect(() => {
    if (!entrevista?.id) return;
    dispatch(setCargaDatos('ContratoFiniquitado', 'get', entrevista.id));
    dispatch(setCargaDatos('Pagador', 'get', entrevista.id));
  }, [dispatch, entrevista?.id, entrevista?.contractId]);

  // Cargar servicios si faltan (igual que en Cancelar.jsx)
  useEffect(() => {
    if (entrevista?.contractId && (!serviciosRedux || serviciosRedux.length === 0)) {
      dispatch(setCargaDatos('contrato', 'get', entrevista.contractId));
    }
  }, [dispatch, entrevista?.contractId, serviciosRedux?.length]);

  // Cabecera (Dirección desde pagador; resto desde defendido)
  useEffect(() => {
    setAtencion({
      Atendidopor: entrevista?.atendidoPor ?? '',
      FechaAtencion: entrevista?.fechaAtencion
        ? moment(entrevista.fechaAtencion).format('DD-MM-YYYY HH:mm')
        : pickFechaEntrevista(entrevista),
      NombrePagador: [pagador?.firstName, pagador?.lastName].filter(Boolean).join(' '),
      RutPagador: pagador?.identifier ?? '',
      TelefonoPagador: pagador?.telephone ?? '',
      EmailPagador: pagador?.email ?? '',
      DireccionPagador: pagador?.address ?? '',
      RegionPagador: general?.regiones?.find(r => r.id === pagador?.idRegion)?.description ?? '',
    });
  }, [entrevista, defendido, pagador, general?.regiones]);

  // ===== Tabla de servicios =====
  const lineaNegocio = entrevista?.lineaNegocioName || entrevista?.lineaNegocio || '';
  const serviciosTabla = useMemo(() => {
    return (serviciosRedux || []).map((s) => ({
      lineaNegocio,
      servicio: s.name,
      costo: Number(s.monto) || 0,
    }));
  }, [serviciosRedux, lineaNegocio]);

  const total = useMemo(
    () => serviciosTabla.reduce((acc, s) => acc + (Number(s.costo) || 0), 0),
    [serviciosTabla]
  );

  // ===== Lógica de finiquito: causas, formatos, cuotas fallback =====
  const getFiniquito = async (contractId) => {
    const _f = await FiniquitoService.FiniquitoContrato(contractId);
    console.log(_f)

    if (_f && typeof _f === 'object') {
      setFiniquito(_f);
      console.log("_f is object")
      await getCausas(contractId, entrevista?.lineaNegocioId, _f.casesCanceled);
    } else {
      console.log("_f is not object")
      await getCausas(contractId, entrevista?.lineaNegocioId, null);
    }
    return _f;
  };

  useEffect(() => {
    if (entrevista?.contractId && pagador) getFiniquito(entrevista.contractId);
    if (entrevista?.finiquitado != null && pagador) setFiniquitado(!!entrevista.finiquitado);
  }, [entrevista?.contractId, entrevista?.finiquitado, pagador]);

  const getCausas = async (contractId, lineId, casesCanceled) => {
    // CAUSAS
    const data = await FiniquitoService.ListCausas(contractId);
    if (Array.isArray(data)) {
      let prev = data.map(item => ({ ...item, finiquitar: false }));
      if (casesCanceled) {
        const ids = JSON.parse(casesCanceled);
        prev = prev.map(item => ids.includes(item.caseIdLegal) ? { ...item, finiquitar: true } : item);
      }
      setCausas(prev);
    }

    // FORMATOS
    const templates = await FiniquitoService.ListTemplateFiniquito(lineId);
    setFormatos(Array.isArray(templates) ? templates.map(normalizeTemplate) : []);


    if (pagador?.identifier && (entrevista?.codigoContrato)) {
      const codCtto = entrevista?.codigoContrato;
      const cuotas = await FiniquitoService.CuotasPendientes(pagador.identifier, codCtto);
      setCuotasShow(true);
      setCuotasMan(Array.isArray(cuotas) ? cuotas : []);
      setSearching(false)

      /* else {
       setCuotasShow(true);
       setCuotasMan([]);
       setSearching(false)
     }*/

    }
    return 'fin';
  };

  // ===== Toggle de causas (checkbox) =====
  const handleToggle = (index) => {
    const updated = [...causas];
    updated[index].finiquitar = !updated[index].finiquitar;
    setCausas(updated);
  };




  // ===== Sincronización formato/html cuando llegan datos =====
  useEffect(() => {
    const codeFromFiniquito = String(finiquito?.codeTemplate ?? '').trim();
    if (!codeFromFiniquito) return;

    if (formatoSeleccionado && formatoSeleccionado === codeFromFiniquito) return;

    if (Array.isArray(formatos) && formatos.length > 0) {
      const hit = formatos.find(f => f.code === codeFromFiniquito);
      if (hit) {
        setFormatoSeleccionado(hit.code);
        setHtmlSeleccionado(finiquito?.html || hit.html || '');
      }
    }
  }, [finiquito?.codeTemplate, finiquito?.html, formatos, formatoSeleccionado]);

  useEffect(() => {
    if (!Array.isArray(formatos) || formatos.length === 0) return;
    if (!formatoSeleccionado) {
      const codeFromFiniquito = String(finiquito?.codeTemplate ?? '').trim();
      if (codeFromFiniquito) {
        const hit = formatos.find(f => f.code === codeFromFiniquito);
        if (hit) {
          setFormatoSeleccionado(hit.code);
          setHtmlSeleccionado(finiquito?.html || hit.html || '');
          return;
        }
      }
      setFormatoSeleccionado(formatos[0].code);
      setHtmlSeleccionado(formatos[0].html || '');
    }
  }, [formatos, finiquito?.codeTemplate, finiquito?.html, formatoSeleccionado]);

  const handleChange = (event) => {
    const codigo = String(event.target.value ?? '').trim();
    setFormatoSeleccionado(codigo);
    const selected = formatos.find(f => f.code === codigo);
    setHtmlSeleccionado(selected?.html || '');
  };

  // ===== Reemplazo de tags en html =====
  useEffect(() => {
    if (!htmlSeleccionado) return;

    const fechaRef = new Date();
    const _f = extraerFecha(fechaRef) || {};

    const codigoContrato = entrevista?.codigoContrato ?? '';
    const sucursalNombre =
      general?.sucursales?.find(x => x.id === defendido?.idBranch)?.name
      ?? '';

    const tipoFuente = (entrevista?.origen ?? 'Remoto').toString();

    let texto = htmlSeleccionado
      .replace(/@dia_entrevista/g, _f.dia ?? '@dia_entrevista')
      .replace(/@mes_entrevista/g, _f.mesTexto ?? '@mes_entrevista')
      .replace(/@anio_entrevista/g, _f.año ?? '@anio_entrevista')
      .replace(/@sucursal/g, sucursalNombre || '@sucursal')
      .replace(/@codigo_contrato/g, (codigoContrato || '').trim() || '@codigo_contrato')
      .replace(/@nombre_pagador/g, `${pagador?.firstName?.trim() || ""} ${pagador?.lastName?.trim() || ""}`.trim() || "@nombre_pagador")
      .replace(/@rut_pagador/g, pagador?.identifier?.trim() || "@rut_pagador")
      .replace(/@nombre_pagador/g, [pagador?.firstName, pagador?.lastName].filter(Boolean).join(' ') || '@nombre_pagador')
      .replace(/@telefono_pagador/g, pagador?.telephone ?? '@telefono_pagador')
      .replace(/@direccion_pagador/g, pagador?.address ?? '@direccion_pagador')
      .replace(/@email_pagador/g, pagador?.email ?? '@email_pagador')
      .replace(/@celular_pagador/g, pagador?.telephone?.trim() || "@celular_pagador")
      .replace(/@ocupacion_pagador/g, pagador?.ocupacion?.trim() || "@ocupacion_pagador")
      .replace(/@estado_civil_pagador/g, general.estadoCivil?.find(x => x.id == pagador?.idEstadoCivil)?.name?.trim() || "@estado_civil_pagador")
      .replace(/@comuna_pagador/g, general.comunas?.find(x => x.id == pagador?.idDistrito)?.description?.trim() || "@comuna_pagador")
      .replace(/@region_pagador/g, general.regiones?.find(x => x.id == pagador?.idRegion)?.description?.trim() || "@region_pagador")
      .replace(/@tipo_fuente/g, tipoFuente || '@tipo_fuente')
      .replace(/@firmas/g, "")//"@firmas"
      .replace(/@nacionalidad/g, general.paises?.find(x => x.id == pagador?.idCountry)?.description?.trim() || "@nacionalidad")
      .replace(/@nacionalidad_pagador/g, general.paises?.find(x => x.id == pagador?.idCountry)?.description?.trim() || "@nacionalidad_pagador")



    if (texto !== htmlSeleccionado) setHtmlSeleccionado(texto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    htmlSeleccionado,
    pagador,
    entrevista
  ]);

  const extraerFecha = (fechaContrato) => {
    const fecha = new Date(fechaContrato);
    const dia = String(fecha.getDate());
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const mesTexto = meses[fecha.getMonth()];
    const año = String(fecha.getFullYear());
    return { dia, mesTexto, año };
  };

  const handleFiniquitar = async () => {
    const sinCuotas = !Array.isArray(cuotasMan) || cuotasMan.length === 0;

    if (sinCuotas) {
      const result = await Swal.fire({
        title: 'Está finiquitando un contrato sin cuotas activas',
        text: '¿Deseas continuar con esta acción?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar',
      });
      if (!result.isConfirmed) return;
    }

    const sfire = Swal.fire({
      title: 'Finiquitando, espere un momento ...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const data = {
      contractId: entrevista?.contractId,
      html: htmlSeleccionado,
      code: formatoSeleccionado,
      cases: causas.filter(c => c.finiquitar).map(c => c.caseIdLegal),
      rut: pagador?.identifier || null,
      codCtto: entrevista?.codigoContrato ?? null,
      siglas: general?.negocios?.find(x => x.id === entrevista?.lineaNegocioId)?.siglas ?? null,
      sucursal: general?.sucursales?.find(x => x.id === defendido?.idBranch)?.description ?? null,
      conCuotas: !sinCuotas
    };

    const invalidFields = Object.entries(data)
      .filter(([key, value]) =>
        key !== 'cases' && ( // ❌ omite la verificación de "cases"
          value === null ||
          value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        )
      )
      .map(([key]) => key);
    console.log(general)
    console.log(data)


    if (invalidFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        html: `Los siguientes campos están vacíos o nulos:<br><b>${invalidFields.join(', ')}</b> Si Ve este mensaje, informe a tecnologia`,
        confirmButtonText: 'Entendido',
      });
      return;
    }

    const _fin = await FiniquitoService.StartFiniquito(data);

    setFiniquito(await getFiniquito(entrevista?.contractId));
    setTimeout(() => {
      let message = '';
      let icon = 'success';

      if (_fin.success) {
        icon = 'success';
        message = _fin.old ? 'Contrato finiquitado con anterioridad' : 'Contrato finiquitado correctamente';
        dispatch(setCargaDatos('ContratoFiniquitado', 'get', entrevista.id));
      } else {
        icon = 'warning';
        message = 'Contrato no finiquitado:';
        if (!_fin.legal) message += '<p>Error en causas</p>';
        if (!_fin.manager) message += '<p>Error en cuotas</p>';
        message += '<p>Contacte a tecnologia@grupodefensa.cl</p>';
      }
      sfire.close();
      Swal.fire({ position: 'center', icon, title: message, showConfirmButton: true });
    }, 500);
  };

  const ShowPDF = async () => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlSeleccionado;

    const opt = {
      margin: 10,
      filename: 'Finiquito.pdf',
      image: { type: 'jpeg', quality: 0.6 },
      html2canvas: { scale: 1, useCORS: true },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    try {
      await html2pdf().set(opt).from(tempElement).save();
    } catch (error) {
      console.error('Error al convertir a PDF:', error);
    }
  };

  // Mostrar "code -- name" en el Select
  const renderFormatoValue = (val) => {
    if (!val) return 'Seleccionar...';
    const f = formatos.find(x => x.code === val);
    return f ? `${f.code} -- ${f.name}` : val;
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: '40px', p: 4 }} >
      <Card sx={{ boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" gutterBottom>Preparación Finiquito</Typography>
            <Button variant='outlined' onClick={() => setHide()}>Volver</Button>
          </Box>

          <Alert className='mt-2 mb-4' severity="warning" hidden={!finiquitado}>
            Contrato Finiquitado.
          </Alert>

          <Divider sx={{ mb: 2 }} />

          {/* Cabecera */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Atendido por:</strong> {atencion.Atendidopor}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Fecha Atención:</strong> {atencion.FechaAtencion}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Nombre:</strong> {atencion.NombrePagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>RUT:</strong> {atencion.RutPagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Email:</strong> {atencion.EmailPagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Teléfono:</strong> {atencion.TelefonoPagador}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} hidden>
              <Typography><strong>Región:</strong> {atencion.RegionPagador ?? ''}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Dirección:</strong> {atencion.DireccionPagador}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>
                <strong>Monto Acordado Contrato: </strong> {formatter.format(total || 0)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla de servicios */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#C8BFE7' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>LINEA DE NEGOCIO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SERVICIO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>COSTO SERVICIO</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {serviciosTabla.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{row.lineaNegocio}</TableCell>
                <TableCell>{row.servicio}</TableCell>
                <TableCell>{formatter.format(Number(row.costo) || 0)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2}></TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 'bold' }}>: Total Servicios</Typography>
                <Typography>{formatter.format(total || 0)}</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* === Tabla de Causas (ID/Título/Finiquitar) === */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#C8BFE7' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID CASE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>TÍTULO CASE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>FINIQUITAR</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {causas.map((row, index) => (
              <TableRow key={row.caseIdLegal ?? index}>
                <TableCell>{row.caseIdLegal}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={!!row.finiquitar}
                    onChange={() => handleToggle(index)}
                    disabled={finiquitado}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Tabla de CUOTAS (versión antigua) */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, height: 400, mt: 4, pb: 1 }} hidden={finiquitado}>
        <LinearProgress hidden={!searching} />
        <Table hidden={cuotasMan.length == 0}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#C8BFE7' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Numero Cuota</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha Vencimiento</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Monto</TableCell>
            </TableRow>
          </TableHead>
          <TableBody >
            {cuotasMan.map((c, index) => (
              <TableRow key={index}>
                <TableCell>{c.numdoc}</TableCell>
                <TableCell>{c.vencimiento}</TableCell>
                <TableCell>{c.saldo.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</TableCell>
              </TableRow>
            ))}
            <TableRow >
              <TableCell></TableCell>
              <TableCell>Total</TableCell>
              <TableCell>
                {cuotasMan.reduce((sum, item) => sum + (Number(item.saldo) || 0), 0)
                  .toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {!finiquitado && cuotasMan.length == 0 && cuotasShow ? (
          <Box sx={{ mt: 3, p: 2, width: '100%', textAlign: 'center', justifyContent: 'center', display: 'flex' }}>
            <Box sx={{ p: 2, width: '50%', textAlign: 'center' }}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography gutterBottom variant="h4">
                  No se encontraron cuotas para finiquitar
                </Typography>
                <Typography gutterBottom variant="h6">
                  Revise el portal de cuotas antes de continuar
                </Typography>
              </Box>

              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Stack direction="row" justifyContent="center" spacing={1}>
                  <Button
                    size='large'
                    variant="contained"
                    color="primary"
                    onClick={() => window.open('http://172.16.10.45:3019/', '_blank', 'noopener,noreferrer')}
                  >
                    Revisar Cuotas
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        ) : null}
      </TableContainer>

      {/* Editor + acciones */}
      <Box sx={{ mt: 3, p: 3, background: 'white', mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }} gutterBottom>Finiquito ss</Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="formato-label">Formatos</InputLabel>
          <Select
            id="formato-select"
            labelId="formato-label"
            disabled={finiquitado}
            value={formatoSeleccionado || ''}
            label="Formatos de contratos"
            onChange={handleChange}
            displayEmpty
            renderValue={renderFormatoValue}
            MenuProps={{
              disablePortal: true,
              slotProps: { paper: { sx: { zIndex: (t) => t.zIndex.modal + 2 } } },
            }}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {formatos.map((f) => (
              <MenuItem key={f.id ?? f.code} value={f.code}>
                {f.code} -- {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {htmlSeleccionado && (
          <ReactQuill
            readOnly={finiquitado}
            theme="snow"
            value={htmlSeleccionado}
            onChange={setHtmlSeleccionado}
          />
        )}

        <Button
          sx={{ mt: 3 }}
          className='me-3'
          variant='outlined'
          onClick={() => ShowPDF()}
          hidden={!(finiquitado || (finiquito && String(finiquito.status) === '1'))}
        >
          Descargar
        </Button>

        {window.ROLID_FINIQUITA.includes(parseInt(cookies.get('rolId'), 10)) && (
          <Button
            sx={{ mt: 3, ms: 3 }}
            variant='outlined'
            onClick={() => handleFiniquitar()}
            disabled={finiquitado || (finiquito && String(finiquito.status) === '1')}
          >
            {(finiquitado || (finiquito && String(finiquito.status) === '1'))
              ? 'Contrato finiquitado'
              : 'Finiquitar'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Finiquitar;
